import type { ForManagerLocationDetail } from "@sidegigllc/api/client";
import { env } from "config/env";
import { useAuth } from "domains/auth";
import { LDProvider, useFlags } from "launchdarkly-react-client-sdk";
import React from "react";
import { useActiveLocation } from "services/locationOrgContext";
import { context, identify, ldClientPromise, options } from "./client";
import { defaultFlags, type FeatureFlagKey, type FeatureFlagValue, type OverhaulPart } from "./featureFlags";

export const LaunchDarklyProvider: React.FC<React.PropsWithChildren> = ({ children }) => (
  <LDProvider
    ldClient={ldClientPromise}
    context={context}
    options={options}
    clientSideID={env.REACT_APP_LAUNCH_DARKLY_DEV_SERVER_URL ? "default" : env.REACT_APP_LAUNCH_DARKLY_ID}
    reactOptions={{ useCamelCaseFlagKeys: false }}
    flags={defaultFlags}
  >
    {children}
  </LDProvider>
);

export const useFeatureFlag = <FlagKey extends FeatureFlagKey>(
  feature: FlagKey,
  options?: { skipDefault?: boolean },
): FeatureFlagValue[FlagKey] => {
  const flags = useFlags<FeatureFlagValue>();
  if (options?.skipDefault) return flags[feature];
  return flags[feature] ?? defaultFlags[feature];
};

export const useMarketplaceOverhaul = (part: OverhaulPart) => {
  const parts = useFeatureFlag("marketplace-overhaul");
  return Array.isArray(parts) && parts.includes(part);
};

export const usePageLocationContext = (location?: ForManagerLocationDetail) => {
  const { analyticsUser } = useAuth();
  const [activeLocation] = useActiveLocation();

  React.useEffect(() => {
    if (!analyticsUser || !location || location.id === activeLocation?.id) return;
    void identify({ user: analyticsUser, location });
    return () => {
      void identify({ user: analyticsUser, location: activeLocation });
    };
  }, [activeLocation, location, analyticsUser]);
};
